import React, { useImperativeHandle, forwardRef, useEffect, useState } from 'react';
import { useErrorInputs } from '../../../hooks/useErrorInputs';
import { useNotification } from '../../../hooks/useNotification';
import { useDataAuth } from '../../../hooks/useDataAuth';
import { useTranslation } from "react-i18next";
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import moment from 'moment';
import { getAssociatedDocument, fetchServiceJson } from '../../../services/common.services';
export const AssociatedInvoice = forwardRef(({ dto, handleInputChange, handleDirectInputChange }, ref) => {
    const [errorInputs, , , validForm] = useErrorInputs({});
    const [sendNotification] = useNotification();
    const [{ userSession }] = useDataAuth();
    const { t } = useTranslation();
    const fieldsRequired = [
        'currency',
        'id_payment_method',
        'id_mean_of_payment',
        'payment_due_date',
        ...(dto.customization_id === '30' ? ['document_id_referenced'] : [])
    ];
    const typeOperations = [
        { code: "30", name: t('commonFields.standart') },
        { code: "32", name: t('commonFields.debitNoteWithoutReference') }
    ];
    const paymentMethods = [
        { code: "1", name: t('commonFields.typeCredit') },
        { code: "2", name: t('commonFields.typeCounted') }
    ]
    const [coins, setCoins] = useState([]);
    const [meansOfPayments, setMeansOfPayments] = useState([]);

    useImperativeHandle(ref, () => ({
        getData: () => dto,
        errorInputs,
        validForm: () => validForm(fieldsRequired, dto)
    }));

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [menuInvoiceData] = await Promise.all([
                    fetchServiceJson("MENU-INVOICE", userSession.token)
                ]);
                if (menuInvoiceData?.data) {
                    menuInvoiceData.data.forEach((item) => {
                        try {
                            const values = JSON.parse(item.value);
                            if (item.field === "coins") {
                                setCoins(values);
                            } else if (item.field === "means_of_payment") {
                                setMeansOfPayments(values);
                            }
                        } catch (jsonError) {
                            console.error("Error parsing JSON for item:", item, jsonError);
                        }
                    });
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        fetchData();
    }, [userSession.token]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'id_mean_of_payment') {
            const selectedPayment = meansOfPayments.find(payment => payment.code === value);
            if (selectedPayment) {
                handleDirectInputChange('mean_of_payment_text', selectedPayment.name);
            }
        }
        handleDirectInputChange(name, value);
    };

    const handleChangeDate = (e, name) => handleDirectInputChange(name, moment(e).format('YYYY/MM/DD'));
    const handleChangeDatePeriod = (date, field) => {
        if (!date) return;
        handleDirectInputChange(field, moment(date).format('YYYY/MM/DD'))
        if (field === "start_period_date") {
            let newEndDate = new Date(date);
            newEndDate.setMonth(newEndDate.getMonth() + 1);
            const today = new Date();
            if (newEndDate > today) {
                newEndDate = today;
            }
            handleDirectInputChange('end_period_date', moment(newEndDate).format('YYYY/MM/DD'))
        }
    };

    const handleSearchInvoice = async (e) => {
        const { name, value } = e.target;
        if (value.length > 0) {
            const response = await getAssociatedDocument(dto.document_id_referenced, dto.id_company_supplier, userSession.token);
            if (response && response.data && response.data.length > 0) {
                const doc = response.data[0];
                handleDirectInputChange('currency', doc.currency)
                handleDirectInputChange('id_payment_method', doc.id_payment_method)
                handleDirectInputChange('id_mean_of_payment', doc.id_mean_of_payment)
                handleDirectInputChange('mean_of_payment_text', doc.mean_of_payment_text)
                handleDirectInputChange('id_company_receiver', doc.id_company_receiver)
                handleDirectInputChange('id_office_receiver', doc.id_office_receiver)
                handleDirectInputChange('address_delivery', doc.address_delivery)
                handleDirectInputChange('uuid_referenced', doc.uuid)
                handleDirectInputChange('id_client', doc.id_client)
                handleDirectInputChange('expedition_date_referenced', moment(doc.expedition_date).format('YYYY/MM/DD'))
            } else {
                sendNotification({ msg: t('messages.documentReferenceInvalid'), variant: 'error' });
                return;
            }
        }
    }

    return (
        <Box sx={{ mb: 2 }}>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required size="small">
                        <InputLabel>{t('commonFields.typeOperation')}</InputLabel>
                        <Select label={t('commonFields.typeOperation')} name="customization_id" value={dto.customization_id} onChange={handleInputChange}>
                            {typeOperations.map((coin, i) => <MenuItem key={i} value={coin.code}>{coin.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 8 }} />
                {dto.customization_id === '30' && (
                    <>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField fullWidth required size="small" label={t('commonFields.documentReferenced')} name="document_id_referenced"
                                value={dto?.document_id_referenced}
                                error={Boolean(errorInputs.document_id_referenced?.isError)}
                                helperText={errorInputs.document_id_referenced?.messageError}
                                onChange={handleInputChange}
                                onBlur={handleSearchInvoice}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label={t('commonFields.expeditionDateDocumentReferenced')}
                                    format="DD/MM/YYYY"
                                    maxDate={moment()}
                                    value={dto.expedition_date_referenced ? moment(dto.expedition_date_referenced)
                                        : null
                                    }
                                    onChange={(e) => handleChangeDate(e, 'expedition_date_referenced')}
                                    slotProps={{
                                        textField: {
                                            id: 'expedition_date_referenced',
                                            name: 'expedition_date_referenced',
                                            size: "small",
                                            fullWidth: true,
                                            required: false,
                                            error: Boolean(errorInputs['expedition_date_referenced']?.isError),
                                            helperText: errorInputs['expedition_date_referenced']?.messageError,
                                            InputLabelProps: { shrink: true }
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>

                    </>
                )}
                {dto.customization_id === '32' && (
                    <>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label={t('commonFields.startDate')}
                                    format="DD/MM/YYYY"
                                    maxDate={moment()}
                                    value={dto.start_period_date ? moment(dto.start_period_date)
                                        : null
                                    }
                                    onChange={(e) => handleChangeDate(e, 'start_period_date')}
                                    slotProps={{
                                        textField: {
                                            id: 'start_period_date',
                                            name: 'start_period_date',
                                            size: "small",
                                            fullWidth: true,
                                            required: false,
                                            error: Boolean(errorInputs['start_period_date']?.isError),
                                            helperText: errorInputs['start_period_date']?.messageError,
                                            InputLabelProps: { shrink: true }
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label={t('commonFields.endDate')}
                                    format="DD/MM/YYYY"
                                    maxDate={moment()}
                                    value={dto.end_period_date ? moment(dto.end_period_date)
                                        : null
                                    }
                                    onChange={(e) => handleChangeDate(e, 'end_period_date')}
                                    slotProps={{
                                        textField: {
                                            id: 'end_period_date',
                                            name: 'end_period_date',
                                            size: "small",
                                            fullWidth: true,
                                            required: false,
                                            disabled: true,
                                            error: Boolean(errorInputs['end_period_date']?.isError),
                                            helperText: errorInputs['end_period_date']?.messageError,
                                            InputLabelProps: { shrink: true }
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </>
                )}

                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required size="small"
                        error={Boolean(errorInputs.currency?.isError)}
                    >
                        <InputLabel>{t('commonFields.currency')}</InputLabel>
                        <Select label={t('commonFields.currency')} name="currency"
                            value={dto.currency}
                            onChange={handleInputChange}
                        >
                            {coins.map((coin, i) => <MenuItem key={i} value={coin.code}>{coin.name}</MenuItem>)}
                        </Select>
                        {errorInputs.currency?.isError && (<FormHelperText>{errorInputs.currency.messageError}</FormHelperText>)}
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required size="small"
                        error={Boolean(errorInputs.id_payment_method?.isError)}
                    >
                        <InputLabel>{t('commonFields.paymentMethods')}</InputLabel>
                        <Select label={t('commonFields.paymentMethods')} name="id_payment_method"
                            value={dto.id_payment_method}
                            onChange={handleInputChange}
                            MenuProps={{ PaperProps: { style: { maxHeight: 200, overflowY: "auto" } } }}
                        >
                            {paymentMethods.map((item) => <MenuItem key={item.code} value={item.code}>{item.name}</MenuItem>)}
                        </Select>
                        {errorInputs.id_payment_method?.isError && <FormHelperText>{errorInputs.id_payment_method.messageError}</FormHelperText>}
                    </FormControl>
                </Grid>

                {dto.id_payment_method === '1' && (
                    <Grid size={{ xs: 12, md: 4 }}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                label={t('commonFields.expirationDate')}
                                format="DD/MM/YYYY"
                                minDate={moment()}
                                value={dto.payment_due_date ? moment(dto.payment_due_date)
                                    : null
                                }
                                onChange={(e) => handleChangeDate(e, 'payment_due_date')}
                                slotProps={{
                                    textField: {
                                        id: 'payment_due_date',
                                        name: 'payment_due_date',
                                        size: "small",
                                        fullWidth: true,
                                        required: false,
                                        error: Boolean(errorInputs['payment_due_date']?.isError),
                                        helperText: errorInputs['payment_due_date']?.messageError,
                                        InputLabelProps: { shrink: true }
                                    }
                                }}
                            />
                        </LocalizationProvider>

                    </Grid>
                )}

                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required size="small" error={Boolean(errorInputs.id_mean_of_payment?.isError)}>
                        <InputLabel>{t('commonFields.meanOfPayment')}</InputLabel>
                        <Select
                            name="id_mean_of_payment"
                            value={dto.id_mean_of_payment || ''}
                            onChange={handleChange}
                            MenuProps={{ PaperProps: { className: 'select-dropdown-scroll' } }}
                        >
                            {meansOfPayments.map((meansOfPayment, i) => <MenuItem key={i} value={meansOfPayment.code}>{meansOfPayment.name}</MenuItem>)}
                        </Select>
                        {errorInputs.id_mean_of_payment?.isError && (<FormHelperText>{errorInputs.id_mean_of_payment.messageError}</FormHelperText>)}
                    </FormControl>
                </Grid>
            </Grid>
        </Box >
    );
});
